import { format, formatDistance } from 'date-fns';
import { toDate, type DateInput } from './date';

const LOCALE = 'en-US';
const DEFAULT_CURRENCY = 'USD';
const DEFAULT_DECIMALS = 2;

/** Display tokens. Override per call when a cell needs a one-off pattern. */
export const DATE_FORMATS = {
  date: 'MM/dd/yyyy',
  dateTime: 'MM/dd/yyyy h:mm a',
  time: 'h:mm a',
  /** "27 Sep 2026" */
  dateMedium: 'd MMM yyyy',
  /** "27 Sep 2026, 1:31 PM" */
  dateTimeMedium: 'd MMM yyyy, h:mm a',
} as const;

export interface AmountOptions {
  /** Fraction digits. Defaults to 2. */
  decimals?: number;
  /** Omit for a plain grouped amount. `true` uses USD; a code uses that currency. */
  currency?: string | true;
}

const formatWith = (value: DateInput, pattern: string): string => {
  const date = toDate(value);
  return date ? format(date, pattern) : '';
};

/** "09/27/2026" */
export const formatDate = (value: DateInput, pattern: string = DATE_FORMATS.date) =>
  formatWith(value, pattern);

/** "09/27/2026 1:31 PM" */
export const formatDateTime = (value: DateInput, pattern: string = DATE_FORMATS.dateTime) =>
  formatWith(value, pattern);

/** "1:31 PM" */
export const formatTime = (value: DateInput, pattern: string = DATE_FORMATS.time) =>
  formatWith(value, pattern);

/** "27 Sep 2026" */
export const formatDateMedium = (value: DateInput) => formatWith(value, DATE_FORMATS.dateMedium);

/** "27 Sep 2026, 1:31 PM" */
export const formatDateTimeMedium = (value: DateInput) =>
  formatWith(value, DATE_FORMATS.dateTimeMedium);

/** "about 1 month ago" */
export const formatRelative = (value: DateInput, now: DateInput = Date.now()) => {
  const date = toDate(value);
  const base = toDate(now);
  if (!date || !base) return '';
  return formatDistance(date, base, { addSuffix: true });
};

const toFiniteNumber = (value: number | null | undefined): number | null =>
  value == null || !Number.isFinite(value) ? null : value;

/** "1,234.50" or "$1,234.50" when `currency` is set. */
export const formatAmount = (value: number | null | undefined, options: AmountOptions = {}) => {
  const amount = toFiniteNumber(value);
  if (amount == null) return '';
  const decimals = options.decimals ?? DEFAULT_DECIMALS;
  const currency = options.currency === true ? DEFAULT_CURRENCY : options.currency;
  return new Intl.NumberFormat(LOCALE, {
    ...(currency ? { style: 'currency' as const, currency } : {}),
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

/** USD with two decimals. Kept as the payments-table alias of `formatAmount`. */
export const formatCurrency = (value: number | null | undefined) =>
  formatAmount(value, { currency: DEFAULT_CURRENCY });

/** Grouped number without forcing two decimals: 1234.5 → "1,234.5". */
export const formatNumber = (value: number | null | undefined, decimals?: number) => {
  const amount = toFiniteNumber(value);
  if (amount == null) return '';
  return new Intl.NumberFormat(LOCALE, {
    ...(decimals != null
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : {}),
  }).format(amount);
};

/** Raw text for a table cell or label. Empty, null, and non-displayable values use `fallback`. */
export const displayValue = (value: unknown, fallback = ''): string => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value.trim() === '' ? fallback : value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === 'boolean') return String(value);
  return fallback;
};
